import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import type {
  ComponentProps,
  FormEventHandler,
} from "react"
import {
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react"
import { AccountInformationForm } from "src/components"
import {
  useAccount,
  usePhones,
  useCreatePhone,
  usePromotePhoneToPrimary,
} from "src/hooks"
import {
  CreatePhoneErrorToast,
  GenericErrorToast,
} from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

type Form = ComponentProps<typeof AccountInformationForm>["form"]

interface Props {
  onSubmitted: FormEventHandler<HTMLFormElement>;
  children?: ComponentProps<typeof AccountInformationForm>["children"];
}

export function AccountInformationPage(props: Props) {
  const {
    onSubmitted,
    children,
  } = props
  const { data: account } = useAccount()

  const createPhone = useCreatePhone()
  const promotePhone = usePromotePhoneToPrimary()
  const { data: phones } = usePhones()

  const { showToast } = useToasterContext()

  const initialValue: Form = useMemo(
    () => ({
      emailAddress: account?.email ?? "",
      phoneNumber: "",
    }),
    [account],
  )

  const [
    form,
    setForm,
  ] = useState<Form>(initialValue)

  useEffect(
    () => {
      setForm(initialValue)
    },
    [
      initialValue,
      setForm,
    ],
  )

  const updateForm = useCallback(
    (value: Partial<Form>) => {
      setForm({
        ...form,
        ...value,
      })
    },
    [
      form,
      setForm,
    ],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        if (!form.phoneNumber) {
          onSubmitted(ev)
          return
        }

        const matchingPhone = (phones ?? []).find(
          (phone) => phone.phone_number === form.phoneNumber && phone.deleted_at === null,
        )

        if (matchingPhone) {
          try {
            if (!promotePhone) {
              throw new Error("cannot create phone")
            }

            await promotePhone(matchingPhone.id)

            onSubmitted(ev)
          } catch {
            showToast(
              "update-phone-error-toast",
              GenericErrorToast,
            )
          }
        } else {
          try {
            if (!createPhone) {
              throw new Error("cannot create phone")
            }

            await createPhone({
              phone_number: form.phoneNumber,
              is_primary: true,
            })

            onSubmitted(ev)
          } catch (e) {
            const sugarmateError = getSugarmateError(e)

            const ErrorToast = injectProps(
              CreatePhoneErrorToast,
              {
                code: sugarmateError?.code,
                status: sugarmateError?.status,
              },
            )

            showToast(
              "create-phone-error-toast",
              ErrorToast,
            )
          }
        }
      })()
    },
    [
      phones,
      form.phoneNumber,
      createPhone,
      promotePhone,
      onSubmitted,
      showToast,
    ],
  )

  return (
    <AccountInformationForm
      form={form}
      updateForm={updateForm}
      onSubmit={onSubmit}
      disableEmail
    >
      {children}
    </AccountInformationForm>
  )
}
